import {Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs';

export interface AppDialogOptions {
    headerText?: string;
    bodyText: string;
    confirmButtonText?: string;
    cancelButtonText?: string;
    danger?: boolean;
}

export interface AppDialogState {
    headerText: string;
    bodyText: string;
    confirmButtonText: string;
    cancelButtonText: string;
    showCancelButton: boolean;
    danger: boolean;
}

interface PendingDialog {
    state: AppDialogState;
    resolve: (confirmed: boolean) => void;
}

@Injectable({providedIn: 'root'})
export class DialogService {
    private readonly dialogSubject = new BehaviorSubject<AppDialogState | null>(null);
    readonly dialog$ = this.dialogSubject.asObservable();
    private readonly queue: PendingDialog[] = [];
    private activeDialog?: PendingDialog;

    get isOpen(): boolean {
        return this.activeDialog !== undefined;
    }

    confirm(options: AppDialogOptions): Promise<boolean> {
        return this.open(options, true);
    }

    async alert(options: AppDialogOptions): Promise<void> {
        await this.open({...options, danger: options.danger ?? false}, false);
    }

    respond(confirmed: boolean): void {
        if (!this.activeDialog) return;

        const completedDialog = this.activeDialog;
        this.activeDialog = undefined;
        this.dialogSubject.next(null);
        completedDialog.resolve(confirmed);
        queueMicrotask(() => this.showNext());
    }

    private open(options: AppDialogOptions, showCancelButton: boolean): Promise<boolean> {
        return new Promise(resolve => {
            this.queue.push({
                state: {
                    headerText: options.headerText || (showCancelButton ? 'Confirm Action' : 'Notice'),
                    bodyText: options.bodyText,
                    confirmButtonText: options.confirmButtonText || (showCancelButton ? 'Confirm' : 'Close'),
                    cancelButtonText: options.cancelButtonText || 'Cancel',
                    showCancelButton,
                    danger: options.danger ?? showCancelButton
                },
                resolve
            });
            this.showNext();
        });
    }

    private showNext(): void {
        if (this.activeDialog || this.queue.length === 0) return;
        this.activeDialog = this.queue.shift();
        this.dialogSubject.next(this.activeDialog.state);
    }
}
